/**
 * @file CompactDropdown.js
 * @description A slim variant of the standard Dropdown for use in tight toolbar spaces.
 * Mirrors the structure and visual language of Dropdown.js but renders at a reduced height,
 * font size, and padding. Supports the same core props minus search, title, and bottom option.
 *
 * @prop {string} defaultValue - The currently selected option label.
 * @prop {Array<{value: string, label: string}>} options - Selectable options.
 * @prop {function} onChange - Called with the selected option value when selection changes.
 * @prop {boolean} disabled - Whether the dropdown is disabled. Default false.
 * @prop {number} tabIndex - Tab order for the trigger element. Default 0.
 * @prop {string} maxOpenHeight - Max height of the option list. Default '200px'.
 * @prop {boolean} overlappingOptions - Whether the list uses absolute positioning. Default true.
 */

import { useState, useEffect } from 'react';
import { useWindowListener } from '../helpers/useWindowListener';
import './CompactDropdown.css';
import { v4 as uuidv4 } from 'uuid';

export function CompactDropdown({
    defaultValue,
    options,
    onChange,
    disabled = false,
    tabIndex = 0,
    maxOpenHeight = '200px',
    overlappingOptions = true,
}) {
    const [showOptionList, setShowOptionList] = useState(false);
    const [selectedText, setSelectedText] = useState(defaultValue);

    useEffect(() => {
        setSelectedText(defaultValue);
    }, [defaultValue]);

    const toggleShowOptionList = event => {
        event.preventDefault();
        if (!disabled) setShowOptionList(prev => !prev);
    };

    useWindowListener('mousedown', event => {
        const cls = event.target.classList;
        if (
            !cls.contains('compactSelectedText') &&
            !cls.contains('compactSelectList') &&
            !cls.contains('compactSelectOption') &&
            !cls.contains('bi')
        ) {
            setShowOptionList(false);
        }
    });

    const handleOptionSelect = option => {
        setSelectedText(option.label);
        setShowOptionList(false);
        onChange(option.value);
    };

    return (
        <div className="compactDropdownWrapper">
            <div
                className={
                    'compactDropdownBox compactSelectedText' +
                    (showOptionList ? ' active' : '') +
                    (disabled ? ' disabled' : '')
                }
                onClick={toggleShowOptionList}
                tabIndex={tabIndex}
                role="combobox"
                aria-expanded={showOptionList}
                aria-haspopup="listbox"
            >
                <div className="compactDropdownLabel">
                    {disabled ? defaultValue : selectedText}
                </div>
                <i
                    className={
                        'bi bi-chevron-' + (showOptionList ? 'up' : 'down')
                    }
                />
            </div>

            {showOptionList && (
                <ul
                    className="compactSelectList"
                    style={{
                        maxHeight: maxOpenHeight,
                        position: overlappingOptions ? 'absolute' : 'relative',
                    }}
                >
                    <div className="mb-1">
                        {options.map(option => (
                            <li
                                className="compactSelectOption"
                                key={uuidv4()}
                                onClick={() => handleOptionSelect(option)}
                            >
                                {option.label}
                            </li>
                        ))}
                    </div>
                </ul>
            )}
        </div>
    );
}
